import { v4 as uuid } from 'uuid'
import {
    type AdminDiscountCodeLookup,
    DiscountType,
} from '@storefronts/common-types'
import {
    exclusiveEndFromInclusiveInputs,
    fromDateAndTimeInputs,
    toDateInputValue,
    toInclusiveEndDateInput,
    toInclusiveEndTimeInput,
    toTimeInputValue,
} from './datetime'
import { findOverlaps, isValidWindow, sortByStart } from './overlap'
import {
    DefaultSpecificPromotion,
    OverlapConflict,
    PromotionSource,
    ScheduleDraft,
    ScheduleEntry,
} from './types'

export type ScheduleFormValues = {
    name: string
    discountCode: string
    discountType: DiscountType
    discountValue: number
    bannerText: string
    startDate: string
    startTime: string
    endDate: string
    endTime: string
}

export type FoundShopifyLookup = Pick<
    Extract<AdminDiscountCodeLookup, { found: true }>,
    | 'title'
    | 'discountType'
    | 'discountValue'
    | 'startsAt'
    | 'endsAt'
    | 'shopifyId'
>

export function emptyScheduleForm(): ScheduleFormValues {
    return {
        name: '',
        discountCode: '',
        discountType: DiscountType.PERCENT,
        discountValue: 0,
        bannerText: '',
        startDate: '',
        startTime: '00:00',
        endDate: '',
        endTime: '23:59',
    }
}

export function formFromEntry(
    entry: ScheduleEntry,
    timeZone?: string,
): ScheduleFormValues {
    return {
        name: entry.name,
        discountCode: entry.discountCode ?? '',
        discountType: entry.discountType,
        discountValue: entry.discountValue,
        bannerText: entry.bannerText ?? '',
        startDate: toDateInputValue(entry.startsAt, timeZone),
        startTime: toTimeInputValue(entry.startsAt, timeZone),
        endDate: toInclusiveEndDateInput(entry.endsAt, timeZone),
        endTime: toInclusiveEndTimeInput(entry.endsAt, timeZone),
    }
}

export function formFromDuplicate(
    entry: ScheduleEntry,
    timeZone?: string,
): ScheduleFormValues {
    return {
        ...formFromEntry(entry, timeZone),
        startDate: '',
        startTime: '00:00',
        endDate: '',
        endTime: '23:59',
    }
}

export function formFromLookup(
    prev: ScheduleFormValues,
    lookup: FoundShopifyLookup,
    timeZone?: string,
): ScheduleFormValues {
    return {
        ...prev,
        name: prev.name.trim() ? prev.name : lookup.title,
        // Shopify reports no plain percent/amount for free shipping or
        // buy-X-get-Y codes; those are "Other" and carry no amount.
        discountType: lookup.discountType
            ? DiscountType[lookup.discountType]
            : DiscountType.OTHER,
        discountValue: lookup.discountType
            ? (lookup.discountValue ?? prev.discountValue)
            : 0,
        startDate: lookup.startsAt
            ? toDateInputValue(lookup.startsAt, timeZone)
            : prev.startDate,
        startTime: lookup.startsAt
            ? toTimeInputValue(lookup.startsAt, timeZone)
            : prev.startTime,
        endDate: lookup.endsAt
            ? toInclusiveEndDateInput(lookup.endsAt, timeZone)
            : prev.endDate,
        endTime: lookup.endsAt
            ? toInclusiveEndTimeInput(lookup.endsAt, timeZone)
            : prev.endTime,
    }
}

export function windowFromForm(
    values: Pick<
        ScheduleFormValues,
        'startDate' | 'startTime' | 'endDate' | 'endTime'
    >,
    timeZone?: string,
): { startsAt: string; endsAt: string } | null {
    const startsAt = fromDateAndTimeInputs(
        values.startDate,
        values.startTime,
        timeZone,
    )
    const endsAt = exclusiveEndFromInclusiveInputs(
        values.endDate,
        values.endTime,
        timeZone,
    )
    if (!startsAt || !endsAt) return null
    return { startsAt, endsAt }
}

export function draftFromForm(
    id: string | null,
    values: ScheduleFormValues,
    timeZone?: string,
): ScheduleDraft | null {
    const window = windowFromForm(values, timeZone)
    if (!window) return null
    return {
        id,
        name: values.name.trim(),
        discountCode: values.discountCode.trim() || null,
        discountType: values.discountType,
        discountValue: values.discountValue,
        bannerText: values.bannerText.trim() || null,
        source: values.discountCode.trim() ? 'SHOPIFY' : 'MANUAL',
        shopifyId: null,
        startsAt: window.startsAt,
        endsAt: window.endsAt,
        copiedShopifyStartsAt: null,
        copiedShopifyEndsAt: null,
    }
}

export function scheduleOverlaps(
    id: string | null,
    values: ScheduleFormValues,
    entries: ScheduleEntry[],
    timeZone?: string,
): OverlapConflict[] {
    const draft = draftFromForm(id, values, timeZone)
    if (!draft) return []
    return findOverlaps(draft, entries)
}

export function formHasDiscount(
    values: Pick<ScheduleFormValues, 'discountType' | 'discountValue'>,
): boolean {
    if (values.discountType === DiscountType.OTHER) return true
    return values.discountValue > 0
}

type DetailsValues = Pick<
    ScheduleFormValues,
    'name' | 'discountCode' | 'discountType' | 'discountValue'
>

/**
 * The first thing stopping the promotion details from saving, or null.
 * A promotion needs a name. The code and the discount are both optional
 * (no code and no amount is a banner-only promotion), but a code with a
 * percent or fixed type needs an amount. "Other" needs nothing else:
 * with a code it is applied at checkout, without one it is banner-only.
 */
export function detailsProblem(values: DetailsValues): string | null {
    if (!values.name.trim()) return 'Enter a name to continue.'
    const hasCode = !!values.discountCode.trim()
    if (hasCode && !formHasDiscount(values)) {
        return 'Enter a discount amount, or choose Other for codes without one.'
    }
    return null
}

export type WindowProblem = {
    kind: 'start' | 'end' | 'order'
    message: string
}

/** The first thing wrong with the dates, or null when they form a window. */
export function windowProblem(
    values: Pick<
        ScheduleFormValues,
        'startDate' | 'startTime' | 'endDate' | 'endTime'
    >,
    timeZone?: string,
): WindowProblem | null {
    const startsAt = fromDateAndTimeInputs(
        values.startDate,
        values.startTime,
        timeZone,
    )
    const endsAt = exclusiveEndFromInclusiveInputs(
        values.endDate,
        values.endTime,
        timeZone,
    )
    if (!startsAt) {
        return { kind: 'start', message: 'Choose a start date and time.' }
    }
    if (!endsAt) {
        return { kind: 'end', message: 'Choose an end date and time.' }
    }
    if (!isValidWindow(startsAt, endsAt)) {
        return { kind: 'order', message: 'The end must be after the start.' }
    }
    return null
}

export function addScheduleDisabledReason(
    values: ScheduleFormValues,
    overlaps: OverlapConflict[],
    timeZone?: string,
): string | null {
    const details = detailsProblem(values)
    if (details) return details
    const window = windowProblem(values, timeZone)
    if (window) return window.message
    if (overlaps.length > 0) return 'Resolve the overlap to continue.'
    return null
}

export function specificFromForm(
    id: string,
    values: Pick<
        ScheduleFormValues,
        | 'name'
        | 'discountCode'
        | 'discountType'
        | 'discountValue'
        | 'bannerText'
    >,
): DefaultSpecificPromotion | null {
    if (detailsProblem(values)) return null
    return {
        id,
        name: values.name.trim(),
        discountCode: values.discountCode.trim() || null,
        discountType: values.discountType,
        discountValue: values.discountValue,
        bannerText: values.bannerText.trim() || null,
    }
}

export function specificDisabledReason(values: DetailsValues): string | null {
    return detailsProblem(values)
}

export function scheduleEntryFromForm(
    id: string,
    values: ScheduleFormValues,
    source: PromotionSource,
    shopifyId: string | null,
    copiedShopifyStartsAt: string | null,
    copiedShopifyEndsAt: string | null,
    timeZone?: string,
): ScheduleEntry | null {
    const window = windowFromForm(values, timeZone)
    if (!window || detailsProblem(values)) return null
    if (!isValidWindow(window.startsAt, window.endsAt)) return null
    return {
        id,
        name: values.name.trim(),
        discountCode: values.discountCode.trim() || null,
        discountType: values.discountType,
        discountValue: values.discountValue,
        bannerText: values.bannerText.trim() || null,
        source,
        shopifyId,
        startsAt: window.startsAt,
        endsAt: window.endsAt,
        copiedShopifyStartsAt,
        copiedShopifyEndsAt,
    }
}

export function upsertScheduleEntry(
    entries: ScheduleEntry[],
    next: ScheduleEntry,
): ScheduleEntry[] {
    return sortByStart([
        ...entries.filter((entry) => entry.id !== next.id),
        next,
    ])
}

export function applyStartToForm(
    values: ScheduleFormValues,
    nextStartsAt: string,
    timeZone?: string,
): ScheduleFormValues {
    return applyWindowToForm(values, { startsAt: nextStartsAt }, timeZone)
}

export function applyWindowToForm(
    values: ScheduleFormValues,
    patch: { startsAt?: string; endsAt?: string },
    timeZone?: string,
): ScheduleFormValues {
    return {
        ...values,
        ...(patch.startsAt
            ? {
                  startDate: toDateInputValue(patch.startsAt, timeZone),
                  startTime: toTimeInputValue(patch.startsAt, timeZone),
              }
            : {}),
        ...(patch.endsAt
            ? {
                  endDate: toInclusiveEndDateInput(patch.endsAt, timeZone),
                  endTime: toInclusiveEndTimeInput(patch.endsAt, timeZone),
              }
            : {}),
    }
}

export function newScheduleId(): string {
    return uuid()
}
